// src/components/schedule/scheduleCron.js
// Build a cron string (optionally with a trailing IANA timezone token, which
// Fugit parses on the backend) from a friendly schedule state. 5 fields:
// minute hour day-of-month month day-of-week [timezone]

const pad = (n) => String(n).padStart(2, "0");
const hm = (time) => {
  const [h, m] = (time || "00:00").split(":").map((x) => parseInt(x, 10));
  return { h: isNaN(h) ? 0 : h, m: isNaN(m) ? 0 : m };
};

export function buildCron(state = {}) {
  const tz = state.timezone ? ` ${state.timezone}` : "";
  switch (state.frequency) {
    case "hourly": {
      const n = Math.max(1, parseInt(state.everyHours, 10) || 1);
      return `0 */${n} * * *`; // tz irrelevant for "every N hours"
    }
    case "daily": {
      const { h, m } = hm(state.time);
      return `${m} ${h} * * *${tz}`;
    }
    case "weekly": {
      const { h, m } = hm(state.time);
      const days = (state.daysOfWeek && state.daysOfWeek.length ? [...state.daysOfWeek] : [1]).sort((a, b) => a - b);
      return `${m} ${h} * * ${days.join(",")}${tz}`;
    }
    case "monthly": {
      const { h, m } = hm(state.time);
      const dom = Math.min(28, Math.max(1, parseInt(state.dayOfMonth, 10) || 1));
      return `${m} ${h} ${dom} * *${tz}`;
    }
    case "advanced":
    default:
      return (state.raw || "").trim();
  }
}

export function parseCron(cron) {
  const raw = (cron || "").trim();
  const parts = raw.split(/\s+/);
  // optional 6th token = timezone
  let timezone;
  if (parts.length === 6) timezone = parts[5];
  if (parts.length === 5 || parts.length === 6) {
    const [min, hour, dom, mon, dow] = parts;
    const time = `${pad(parseInt(hour, 10))}:${pad(parseInt(min, 10))}`;
    const simpleNums = (s) => /^\d+$/.test(s);
    // hourly: 0 */N * * *
    const hourlyMatch = min === "0" && /^\*\/\d+$/.test(hour) && dom === "*" && mon === "*" && dow === "*";
    if (hourlyMatch) return { frequency: "hourly", everyHours: parseInt(hour.split("/")[1], 10) };
    // monthly: m h DOM * *
    if (simpleNums(min) && simpleNums(hour) && simpleNums(dom) && mon === "*" && dow === "*")
      return { frequency: "monthly", time, dayOfMonth: parseInt(dom, 10), timezone };
    // weekly: m h * * DOW(,DOW)
    if (simpleNums(min) && simpleNums(hour) && dom === "*" && mon === "*" && /^\d+(,\d+)*$/.test(dow))
      return { frequency: "weekly", time, daysOfWeek: dow.split(",").map((n) => parseInt(n, 10)), timezone };
    // daily: m h * * *
    if (simpleNums(min) && simpleNums(hour) && dom === "*" && mon === "*" && dow === "*")
      return { frequency: "daily", time, timezone };
  }
  return { frequency: "advanced", raw };
}

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export function describeCron(state = {}) {
  const tz = state.timezone ? ` (${state.timezone})` : "";
  switch (state.frequency) {
    case "hourly": return `Every ${state.everyHours || 1} hour(s)`;
    case "daily": return `Daily at ${state.time || "00:00"}${tz}`;
    case "weekly": return `Weekly on ${(state.daysOfWeek || []).map((d) => DOW[d]).join(", ")} at ${state.time || "00:00"}${tz}`;
    case "monthly": return `Monthly on day ${state.dayOfMonth || 1} at ${state.time || "00:00"}${tz}`;
    default: return state.raw || "—";
  }
}
